<template>
  <div class="max-w-3xl mx-auto my-5 border rounded-2xl px-6 py-6 bg-white dark:bg-gray-600 dark:border-gray-600">
    <div class="flex items-start justify-between gap-4 mb-5">
      <h2 class="text-2xl font-semibold dark:text-gray-100">{{ editMode ? 'Edit Your Profile' : 'Your Profile' }}
      </h2>
      <div class="shrink-0 text-right">
        <button v-if="!editMode" @click="toggleEditMode"
          class="dark:text-gray-600 dark:hover:text-gray-100 dark:bg-yellow-300 dark:hover:bg-yellow-600 px-6 py-2 border border-transparent text-base font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-700">
          Edit
        </button>
        <button v-else @click="toggleEditMode"
          class="px-6 py-2 mr-4 bg-red-500 hover:bg-red-700 text-white font-bold rounded">
          Cancel
        </button>
        <button v-if="editMode" @click="updateProfile"
          class="px-6 py-2 border border-transparent text-base font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-700">Update
          Profile</button>
      </div>
    </div>
    <div v-if="phoneRequired"
      class="mb-4 p-3 rounded-md border border-yellow-400 bg-yellow-50 dark:bg-yellow-900/30 dark:border-yellow-500">
      <p class="text-sm text-yellow-900 dark:text-yellow-100">
        A work phone number is required on your account before you can submit support tickets. Please enter your work
        phone below.
      </p>
    </div>
    <form @submit.prevent="editMode && updateProfile()">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- User Information -->
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Username</label>
          <input type="text" v-model="user.username" disabled class="mt-1 block w-full rounded-md border-gray-300 shadow-sm 
                bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 
                disabled:cursor-not-allowed disabled:opacity-70
              ">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">First Name</label>
          <input type="text" v-model="user.firstName" disabled class="mt-1 block w-full rounded-md border-gray-300 shadow-sm 
                bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 
                disabled:cursor-not-allowed disabled:opacity-70
              ">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Last Name</label>
          <input type="text" v-model="user.lastName" disabled class="mt-1 block w-full rounded-md border-gray-300 shadow-sm 
                bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 
                disabled:cursor-not-allowed disabled:opacity-70
              ">
        </div>
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Email Address</label>
          <input type="email" v-model="user.email" disabled class="mt-1 block w-full rounded-md border-gray-300 shadow-sm 
                bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 
                disabled:cursor-not-allowed disabled:opacity-70
              ">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Phone Primary <span
              class="text-red-500">*</span></label>
          <input type="tel" v-model="addressbookUser.phone_primary" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]">
          <p v-if="primaryPhoneError && editMode" class="text-red-500 text-xs mt-1">{{ primaryPhoneError }}</p>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Phone Primary Ext</label>
          <input type="text" v-model="addressbookUser.phone_primary_ext" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Phone Secondary</label>
          <input type="tel" v-model="addressbookUser.phone_secondary" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]">
          <p v-if="secondaryPhoneError && editMode" class="text-red-500 text-xs mt-1">{{ secondaryPhoneError }}</p>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Phone Secondary Ext</label>
          <input type="text" v-model="addressbookUser.phone_secondary_ext" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]">
        </div>
        <!-- Password Change -->
        <div class="md:col-span-2">
          <h3 class="text-xl font-semibold mt-8 mb-5 dark:text-gray-100">Change Password (Leave blank if no password
            change desired)</h3>
        </div>
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">New Password</label>
          <input type="password" v-model="user.newPassword" :disabled="!editMode"
            class="dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 mt-1 block w-full rounded-md border-gray-300 shadow-sm">
        </div>
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Confirm Password</label>
          <input type="password" @blur="validatePasswords" v-model="user.confirmPassword" :disabled="!editMode"
            class="dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 mt-1 block w-full rounded-md border-gray-300 shadow-sm">
          <p v-if="passwordError && editMode" class="text-red-500 text-xs mt-1">{{ passwordError }}</p>
        </div>
        <div class="md:col-span-2 text-right">
          <button v-if="!editMode" @click="toggleEditMode"
            class="dark:text-gray-600 dark:hover:text-gray-100 dark:bg-yellow-300 dark:hover:bg-yellow-600 mt-6 px-6 py-2 border border-transparent text-base font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-700">
            Edit
          </button>
          <button v-else @click="toggleEditMode"
            class="mt-6 px-6 py-2 mr-4 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded">
            Cancel
          </button>
          <button v-if="editMode" type="submit"
            class="mt-6 px-6 py-2 border border-transparent text-base font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-700">Update
            Profile</button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import axios from 'axios';
export default {
  data() {
    return {
      user: {
        username: '',
        firstName: '',
        lastName: '',
        email: '',
        initials: '',
        // cellEmail: '',
        mobilePhone: '',
        newPassword: '',
        confirmPassword: ''
      },
      addressbookUser: {},
      editMode: false,
      primaryPhoneError: '',
      secondaryPhoneError: '',
      phoneError: '',
      passwordError: '',
    };
  },
  computed: {
    phoneRequired() {
      return this.$route.query.phonerequired === '1';
    }
  },
  methods: {
    toggleEditMode() {
      this.editMode = !this.editMode;
      if (!this.editMode) {
        this.fetchUserInfo();
        this.newPassword = '';
        this.confirmPassword = '';
      }
    },
    async updateProfile() {
      if (this.validatePhone() && this.validatePasswords()) {
        this.$updateLoading(1);
        const userId = localStorage.getItem('user_id');
        const profilePayload = {
          ...this.user,
          mobilePhone: this.addressbookUser.phone_primary || '',
          extension: this.addressbookUser.phone_primary_ext || '',
          secondaryPhone: this.addressbookUser.phone_secondary || '',
          secondaryExt: this.addressbookUser.phone_secondary_ext || '',
        };
        try {
          await axios.put(`extuser/${userId}`, { user: profilePayload });
          this.editMode = false;
          await this.fetchUserInfo();
          if (this.phoneRequired) {
            const redirect = localStorage.getItem('redirectAfterLogin');
            localStorage.removeItem('redirectAfterLogin');
            const redirectPath = redirect ? JSON.parse(redirect).path : '/';
            this.$router.replace(redirectPath);
          }
        } catch (error) {
          console.error('There was an error updating the profile:', error);
          const msg = error?.response?.data?.message || 'Failed to update profile.';
          alert(msg);
        } finally {
          this.$updateLoading(-1);
        }
      }
    },
    validatePhone() {
      const re = /^(\d{3}-\d{3}-\d{4}|\d{10})$/;
      if (!re.test(this.addressbookUser.phone_primary || '')) {
        this.primaryPhoneError = 'Invalid phone format. Use 000-000-0000 or 0000000000.';
        return false;
      }
      this.primaryPhoneError = '';
      const secondary = (this.addressbookUser.phone_secondary || '').trim();
      if (secondary && !re.test(secondary)) {
        this.secondaryPhoneError = 'Invalid phone format. Use 000-000-0000 or 0000000000.';
        return false;
      }
      this.secondaryPhoneError = '';
      return true;
    },
    validatePasswords() {
      if (this.user.newPassword !== this.user.confirmPassword) {
        this.passwordError = 'Passwords do not match.';
        return false;
      }
      this.passwordError = '';
      return true;
    },
    async fetchUserInfo() {
      this.$updateLoading(1);
      try {
        const user_id = localStorage.getItem('user_id');
        const [currentUserAddressbook, response] = await Promise.all([
          axios.post(`requesters/getuser`, { id: user_id }),
          axios.get(`extuser/profile/${user_id}`),
        ]);
        this.addressbookUser = (currentUserAddressbook.data && typeof currentUserAddressbook.data === 'object')
          ? currentUserAddressbook.data
          : {};
        this.user.firstName = response.data.firstname;
        this.user.lastName = response.data.lastname;
        this.user.email = response.data.email;
        this.user.username = response.data.username;
      } finally {
        this.$updateLoading(-1);
      }
    }
  },
  async mounted() {
    await this.fetchUserInfo();
    if (this.phoneRequired) {
      this.editMode = true;
    }
  }
};
</script>